import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Truck, Users, Sparkles, Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { SectionHeader } from "@/components/marketplace/SectionHeader";

export function About() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <span className="eyebrow">Our Story</span>
      <h1 className="mt-2 text-4xl font-serif font-bold text-stone-900">About Shri Vrindavan Shop</h1>
      <p className="mt-4 text-stone-600 leading-relaxed">
        Shri Vrindavan Shop is India's digital marketplace for spiritual, pooja, Braj and Sanatan products. Our mission
        is to bring the devotion of Braj — Vrindavan, Mathura and Govardhan — into every home, while empowering the
        artisans, manufacturers and vendors who craft these sacred products.
      </p>
      <p className="mt-3 text-stone-600 leading-relaxed">
        We connect customers, business buyers, temples and ashrams with verified sellers across a growing catalog of
        malas, vigrah, pooja samagri, bhagwan vastra, books, handicrafts and raw materials — with both B2C retail and
        B2B wholesale.
      </p>
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        {[[ShieldCheck, "Verified Vendors"], [Truck, "Pan India Delivery"], [Users, "Growing Community"]].map(([Icon, t]) => (
          <div key={t} className="rounded-xl border border-border bg-card p-5 text-center">
            <Icon className="mx-auto text-secondary mb-2" size={24} />
            <div className="font-semibold text-stone-900">{t}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 inline-block text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
        This is Step 1 (foundation) of the platform. Payments, shipping, GST and settlements activate in later phases.
      </div>
    </div>
  );
}

export function Contact() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <SectionHeader eyebrow="Get in Touch" title="Contact Us" subtitle="We'd love to hear from you." />
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-4">
          {[[Mail, "Email", "support@shrivrindavanshop.com"], [Phone, "Helpline", "+91 00000 00000"], [MapPin, "Location", "Vrindavan, Uttar Pradesh, India"]].map(([Icon, l, v]) => (
            <div key={l} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
              <div className="h-11 w-11 rounded-lg bg-accent text-secondary grid place-items-center"><Icon size={20} /></div>
              <div><div className="text-xs text-stone-500">{l}</div><div className="font-medium text-stone-800">{v}</div></div>
            </div>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); toast.success("Message sent (demo). We'll get back to you soon."); }} className="rounded-xl border border-border bg-card p-6 space-y-3" data-testid="contact-form">
          <input required placeholder="Your name" data-testid="contact-name" className="w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
          <input required type="email" placeholder="Your email" data-testid="contact-email" className="w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
          <textarea required rows={4} placeholder="Your message" data-testid="contact-message" className="w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
          <button data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3"><Send size={16} /> Send Message</button>
        </form>
      </div>
    </div>
  );
}

const LEGAL = {
  terms: {
    title: "Terms & Conditions",
    body: "By using Shri Vrindavan Shop you agree to our marketplace terms. This is a Step 1 foundation build; full legally-reviewed terms will be published before commercial launch. Products are listed by independent vendors. Prices are shown in INR.",
  },
  privacy: {
    title: "Privacy Policy",
    body: "We respect your privacy. Account information is used only to operate your account and orders. We never expose payment secrets or private KYC documents. A complete privacy policy will be published before launch.",
  },
  "refund-policy": {
    title: "Refund Policy",
    body: "Refund and return handling will be enabled with the orders & payments module in a later phase. Vendor-specific refund windows will be clearly displayed at that time.",
  },
  "shipping-policy": {
    title: "Shipping Policy",
    body: "We plan pan-India delivery through vendor and platform logistics. Live shipping rates, tracking and estimated delivery activate with the shipping module in a later phase.",
  },
  "vendor-policy": {
    title: "Vendor Policy",
    body: "Vendors must be verified (KYC) to sell. Commission, settlement cycles and seller obligations will be formalized with the vendor settlement module. Only genuine, non-copyrighted products are permitted.",
  },
};

export function Legal({ slug }) {
  const data = LEGAL[slug] || LEGAL.terms;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-3xl font-serif font-bold text-stone-900">{data.title}</h1>
      <p className="mt-4 text-stone-600 leading-relaxed">{data.body}</p>
      <div className="mt-6 inline-block text-sm text-secondary bg-secondary/5 border border-secondary/20 rounded-full px-4 py-1.5">
        Complete policy content will be finalized in a later development phase.
      </div>
      <div className="mt-8"><Link to="/" className="text-sm font-semibold text-stone-700 hover:text-secondary">← Back to Home</Link></div>
    </div>
  );
}
